<template>
  <div class="font-bold text-info-blue-100 mb-8 p-1">
    <div
      v-if="!loadingFreeBicycles"
      :class="[ isBicycleAvailable
        ? 'text-success-green'
        : 'text-red-100', 'mb-2' ]"
    >
      <p>{{ isBicycleFreeText }}</p>
    </div>
    <div v-else class="flex justify-left p-2">
      <Spinner class="w-1 h-1" />
    </div>
    <div v-if="!loadingVisibilityPlanningCapacityDto">
      <p>{{ capacityText }}</p>
      <p>{{ usageText }}</p>
      <p>{{ quotaText }}</p>
    </div>
    <div v-else class="flex justify-left p-2">
      <Spinner class="w-1 h-1" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, reactive, unref, watchEffect,
} from 'vue';
import { type Filter, type ProviderParams, useProvider } from '@utils/table/hooks/provider';
import { useQueryRef } from '@hooks/url';
import Spinner from '@utils/Spinner.vue';
import { useI18n } from 'vue-i18n';
import type {
  Region,
  VPTimespanEntry,
  VisibilityLocation,
  VisibilityPlanningModalMode,
  VisibilityLocationOption,
  EnrichedVisibilityBicycleDto,
  VisibilityPlanningCapacityDto,
} from '@/entities/Entities';

interface Props {
  modalMode?: VisibilityPlanningModalMode;
  entry: VPTimespanEntry;
  region: Region | string | undefined;
  location: VisibilityLocation | string | undefined;
  locations: VisibilityLocationOption[] | undefined;
  visibilityBicycle: string | undefined;
}
const props = defineProps<Props>();

const { t } = useI18n();

// Computed property for startTime in ISO format
const startTimeISO = computed(() => {
  const start = props.entry?.time?.start;
  if (start) {
    const dateStart = new Date(start);
    if (!Number.isNaN(dateStart.getTime())) {
      return dateStart.toISOString();
    }
  }
  return '';
});

const endTimeISO = computed(() => {
  const end = props.entry?.time?.end;
  if (end) {
    const newDate = new Date(end);
    if (!Number.isNaN(newDate.getTime())) {
      return newDate.toISOString();
    }
  }
  return '';
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  region: props.region ?? '',
  startTime: startTimeISO.value,
  endTime: endTimeISO.value,
  location: props.entry?.location ?? '',
  bicycle: props.visibilityBicycle ?? '',
}));

const totalFilter = computed(() => ({
  ...fixedFilter.value,
  ...filter.value,
}));

const providerParams = reactive({
  filter: totalFilter,
});

const {
  data: visibilityPlanningCapacityDto,
  loading: loadingVisibilityPlanningCapacityDto,
} = useProvider<VisibilityPlanningCapacityDto>('/visibility_plannings/capacity', providerParams);

const filterBicycles = computed(
  () =>
    ({
      region: props.region ?? '',
      availableFrom: startTimeISO.value ?? '',
      availableTo: endTimeISO.value ?? '',
    } as Filter),
);

const paramsBicycles: ProviderParams = reactive({
  filter: {} as Filter,
  sort: {
    sortKey: 'visibilityBicycle.number',
    sortDirection: 'ASC',
  },
});

watchEffect(() => {
  paramsBicycles.filter = filterBicycles.value;
});

const {
  data: visibilityBicyclesFree,
  loading: loadingFreeBicycles,
} = useProvider('/visibility_bicycles/tracker/free', paramsBicycles, { immediate: false });

const records = computed(() => unref(visibilityBicyclesFree)?.['hydra:member']);

const isBicycleAvailable = computed(() => {
  if (!records.value) return false;

  return !!records.value?.find(
    (vb: EnrichedVisibilityBicycleDto) => vb.visibilityBicycle?.['@id'] === props.visibilityBicycle,
  );
});
const isBicycleFreeText = computed(() => (`${isBicycleAvailable.value
  ? `${t('pages.visibility.planning.modal.direct.bicycleIsAvailable')}`
  : `${t('pages.visibility.planning.modal.direct.bicycleIsNotAvailable')}`}${
  isBicycleAvailable.value ? ' ✅' : ' ❌'}`));

const capacityText = computed(() => {
  if (!visibilityPlanningCapacityDto.value) return '';

  const textAvailable = Math.max(visibilityPlanningCapacityDto.value.availableBicycles ?? 0, 0);
  return `${textAvailable} / ${visibilityPlanningCapacityDto.value.totalBicycles} ${t(
    'pages.visibility.planning.modal.capacityAreAvailable',
  )}`;
});

const usageText = computed(() => {
  if (Number.isNaN(visibilityPlanningCapacityDto.value?.usedBicyclesOnLocation)) return '';
  return `${visibilityPlanningCapacityDto.value?.usedBicyclesOnLocation} ${t(
    'pages.visibility.planning.modal.usedOnLocation',
  )}`;
});

const quotaText = computed(() => {
  const selLoc = props.locations?.find((l) => l['@id'] === props.entry.location);
  if (!selLoc?.quota) return '';
  return `${selLoc.quota} ${t('pages.visibility.planning.modal.recommendedOnLocation')}`;
});
</script>

<style lang="scss" scoped></style>
